/**
 * 工厂端 - 发票（商品票）
 *
 * 工厂作为商品销售方，向消费者开具 13% 商品票（平台受授权代开）。
 * 订单号可点击跳转订单详情；发票号可点击预览数电票图片。
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Table, Badge, useApi, money, time, Loading, Button } from '../../components/ui'
import { HelpBubble } from '../../components/HelpBubble'
import { api } from '../../api/client'

interface Invoice {
  invoice_id: string
  order_id: string
  invoice_category: string
  invoice_type: string
  issuer_id: string
  issuer_role: string
  amount: string
  tax_rate: string
  remark: string
  item_name: string
  invoice_no: string | null
  pdf_url: string | null
  status: string
  issued_at: string | null
}

function categoryLabel(c: string): string {
  if (c === 'GOODS') return '商品票'
  if (c === 'SERVICE_FEE') return '服务费票'
  return c
}

function taxLabel(rate: string): string {
  const n = Number(rate)
  return `${(n * 100).toFixed(0)}%`
}

export function FactoryInvoices() {
  const nav = useNavigate()
  const list = useApi<Invoice[]>(async () => api.get('/invoices'), [])
  const [preview, setPreview] = useState<Invoice | null>(null)

  const totalAmount = (list.data ?? [])
    .filter((i) => i.status === 'ISSUED')
    .reduce((sum, i) => sum + Number(i.amount), 0)

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>发票（商品票）</h2>

      <div style={{ background: '#fef9c3', border: '1px solid #fde68a', color: '#854d0e', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
        工厂作为商品销售方，向消费者开具 13% 商品票（平台受授权代开）。服务费票由商家开具。点订单号看订单详情，点发票号预览数电票。
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 16 }}>
        <MetricCard label="发票总数" value={String(list.data?.length ?? 0)} />
        <MetricCard label="已开具金额" value={money(totalAmount)} />
        <MetricCard label="开票中/失败" value={String((list.data ?? []).filter((i) => i.status === 'ISSUING' || i.status === 'FAILED').length)} />
      </div>

      <Card title={`商品发票（${list.data?.length ?? 0}）`}>
        {list.loading ? (
          <Loading />
        ) : (
          <Table<Invoice>
            rows={list.data ?? []}
            empty="暂无发票"
            columns={[
              { key: 'invoice_no', title: '发票号', render: (r) => r.invoice_no ? <a onClick={() => setPreview(r)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.invoice_no}</a> : '-' },
              { key: 'order_id', title: '订单号', render: (r) => <a onClick={() => nav(`/factory/shipping?detail=${r.order_id}`)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.order_id}</a> },
              { key: 'invoice_category', title: '类别', render: (r) => categoryLabel(r.invoice_category) },
              { key: 'amount', title: '金额', render: (r) => money(r.amount) },
              { key: 'tax_rate', title: '税率', render: (r) => taxLabel(r.tax_rate) },
              { key: 'item_name', title: '品目' },
              { key: 'issued_at', title: '开票时间', render: (r) => (r.issued_at ? time(r.issued_at) : '-') },
              { key: 'status', title: '状态', render: (r) => <Badge status={r.status} /> },
            ]}
          />
        )}
      </Card>

      {/* 发票预览弹层 */}
      {preview && (
        <div onClick={() => setPreview(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, maxWidth: 440, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <strong>{categoryLabel(preview.invoice_category)}预览</strong>
              <Button size="sm" variant="default" onClick={() => setPreview(null)}>关闭</Button>
            </div>
            {preview.pdf_url && preview.status === 'ISSUED' ? (
              <img src={preview.pdf_url} alt={preview.invoice_no ?? ''} style={{ width: '100%', display: 'block' }} />
            ) : (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>无预览（{preview.status}）</div>
            )}
            <div style={{ padding: 12, fontSize: 12, color: '#666' }}>
              <div>发票号：{preview.invoice_no ?? '-'}</div>
              <div>订单：<a onClick={() => nav(`/factory/shipping?detail=${preview.order_id}`)} style={{ color: '#2563eb', cursor: 'pointer' }}>{preview.order_id}</a></div>
              <div>金额：{money(preview.amount)} · 税率：{taxLabel(preview.tax_rate)}</div>
              <div>备注：{preview.remark}</div>
            </div>
          </div>
        </div>
      )}

      <HelpBubble pageKey="factory-invoices" title="发票（商品票）">
        <p>工厂作为商品销售方，向消费者开具 <b>13% 商品票</b>（平台受授权代开）。服务费票由商家开具。</p>
        <p>点发票号预览数电票图片，点订单号看订单详情。</p>
      </HelpBubble>
    </div>
  )
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: '#fff', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' }}>
      <div style={{ color: '#888', fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  )
}
